import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import { getHeroes, getProjects, getGalleryLabels } from '../services/api';
import './Portfolio.css';

// Software Development Projects - Using actual hero images from client websites
const softwareProjects = [
  {
    id: 'armory360',
    title: 'Armory360',
    client: 'Armory360',
    description: 'Privacy-first iPhone and iPad app for firearm owners. Tracks firearms, ammo inventory, range days, drills, maintenance and documents — all encrypted on-device behind PIN and Face ID, with no account, no analytics and optional end-to-end encrypted iCloud sync. Includes a par timer, target scoring, NFC safe checks, and insurance/estate PDF reports. Available in English and Spanish.',
    technologies: ['iOS', 'iPadOS', 'Swift', 'On-Device Encryption', 'iCloud Sync', 'NFC', 'In-App Purchases'],
    image: 'https://armory360.app/og.jpg',
    url: 'https://armory360.app',
    features: ['Firearm Inventory', 'Ammo Tracking', 'Range Logs', 'Maintenance Reminders', 'Documents Wallet', 'Par Timer & Drills'],
    category: 'mobile'
  },
  {
    id: 'sage-southern-cuisine',
    title: 'Sage Southern Cuisine',
    client: 'Sage Southern Cuisine',
    description: 'Restaurant website for a scratch-made Southern comfort food kitchen in West Nyack, NY. Features the full menu, online ordering for pickup, location and hours, and a warm brand-driven design built around the restaurant\'s signature dishes.',
    technologies: ['React', 'Online Ordering', 'Responsive Design', 'SEO Optimized'],
    image: 'https://www.sagesoutherncuisine.com/images/hero-home.jpg',
    url: 'https://www.sagesoutherncuisine.com',
    category: 'website'
  },
  {
    id: 'ccb-website',
    title: 'CCB NYC Website',
    client: 'CCB NYC Church',
    description: 'Modern, responsive church website featuring service times, event calendar, sermon archives, ministry information, and online giving integration.',
    technologies: ['React', 'Node.js', 'Responsive Design', 'CMS'],
    image: 'https://www.ccb-nyc.com/images/home-hero.jpg',
    url: 'https://www.ccb-nyc.com',
    category: 'website'
  },
  {
    id: 'ccb-app',
    title: 'CCB NYC Mobile App',
    client: 'CCB NYC Church',
    description: 'Church community mobile application for iOS and Android. Features sermon streaming, event notifications, prayer requests, and community engagement tools.',
    technologies: ['React Native', 'iOS', 'Android', 'Push Notifications'],
    image: 'https://www.ccb-nyc.com/images/home-hero.jpg',
    category: 'mobile'
  },
  {
    id: 'hoadesk',
    title: 'HOAdesk',
    client: 'HOAdesk',
    description: 'AI-powered HOA management platform for small communities. Board meetings recorded in the browser or mobile app become structured minutes in minutes. Includes resident dues payments (card and ACH), a resident iOS/Android app and web portal, AI compliance monitoring against state HOA law, violation tracking, document vault, polls and voting, and QuickBooks Online sync.',
    technologies: ['Next.js', 'React Native', 'Supabase', 'PostgreSQL', 'Stripe', 'AI/LLM', 'QuickBooks API'],
    image: '/images/portfolio/hoadesk-hero.jpg',
    url: 'https://hoadesk.io',
    features: ['AI Meeting Minutes', 'Resident Payments', 'Resident Mobile App', 'Compliance Monitor', 'Violation Tracking', 'Multi-Tenant SaaS'],
    category: 'portal'
  },
  {
    id: 'djoz-productions',
    title: 'DJOZ Productions',
    client: 'DJOZ Productions',
    description: 'Creative portfolio website for a DJ and music production company. Features event gallery, booking system, music samples, and dynamic visual design that captures the energy of live events.',
    technologies: ['React', 'Responsive Design', 'Media Integration'],
    image: 'https://djoz.productions/images/oz.png',
    url: 'https://djoz.productions',
    category: 'website'
  },
  {
    id: 'scribbles-learning',
    title: 'Scribbles Learning Center',
    client: 'Scribbles Learning Center',
    description: 'Engaging website for a children\'s learning center in Edgewater, NJ. Features program information, enrollment forms, video introduction, and a playful design that appeals to parents and children alike.',
    technologies: ['React', 'Node.js', 'Responsive Design'],
    image: 'https://scribbles-learning-center.vercel.app/logo.png',
    url: 'https://scribbles-learning-center.vercel.app',
    category: 'website'
  },
  {
    id: 'gvc-website',
    title: 'GVC Website',
    client: 'GVC Transportation',
    description: 'Professional website for a school bus transportation company serving the Bronx. Features service information, route details, safety protocols, and contact forms for parents and schools.',
    technologies: ['React', 'Vite', 'Responsive Design'],
    image: '/images/portfolio/gvc-hero.jpg',
    url: 'https://gvcbus.com',
    category: 'website'
  },
  {
    id: 'svt-portal',
    title: 'SVT Portal & Mobile App',
    client: 'SVT Consulting',
    description: 'Internal business management system handling client databases, service calls, invoicing, HR functions, and team communication. Includes iOS app for field technicians.',
    technologies: ['React', 'React Native', 'Node.js', 'PostgreSQL', 'AWS S3', 'Push Notifications', 'iOS'],
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop',
    features: ['Client Management', 'Service Call Tracking', 'Invoicing', 'HR Module', 'Real-time Notifications', 'iOS App'],
    category: 'portal'
  },
  {
    id: 'gvc-portal',
    title: 'GVC Portal & CMS',
    client: 'GVC Transportation',
    description: 'Custom content management system and operations portal for managing routes, schedules, driver assignments, and parent communications.',
    technologies: ['React', 'Node.js', 'PostgreSQL', 'AWS S3'],
    image: '/images/portfolio/gvc-fleet.jpg',
    features: ['Route Management', 'Driver Portal', 'Admin Dashboard', 'Real-time Updates'],
    category: 'portal'
  }
];

const Portfolio = () => {
  const [hero, setHero] = useState(null);
  const [projects, setProjects] = useState([]);
  const [labels, setLabels] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [softwareFilter, setSoftwareFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [heroData, projectsData, labelsData] = await Promise.all([
          getHeroes(),
          getProjects(),
          getGalleryLabels()
        ]);
        setHero(heroData.portfolio);
        setProjects(projectsData);
        setLabels(labelsData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredProjects = activeFilter === 'all' 
    ? projects 
    : projects.filter(p => 
        p.services_provided?.includes(activeFilter) || 
        p.labels?.some(l => l.slug === activeFilter)
      );

  const filteredSoftwareProjects = softwareFilter === 'all'
    ? softwareProjects
    : softwareProjects.filter(p => p.category === softwareFilter);

  const serviceFilters = [
    { key: 'all', name: 'All Projects' },
    { key: 'Audio/Video', name: 'Audio/Video' },
    { key: 'CCTV', name: 'Security' },
    { key: 'Automation', name: 'Automation' },
  ];

  const softwareFilters = [
    { key: 'all', name: 'All' },
    { key: 'website', name: 'Websites' },
    { key: 'mobile', name: 'Mobile Apps' },
    { key: 'portal', name: 'Portals & CRMs' },
  ];

  return (
    <div className="portfolio-page">
      <Hero
        title="Our Portfolio"
        subtitle="Custom software solutions and professional installations"
        backgroundImage={hero?.image_url}
        ctaText="Start Your Project"
        ctaLink="/contact"
        height="medium"
        overlay={0.7}
      />

      {/* Software Development Section */}
      <section className="section software-section">
        <div className="container">
          <div className="section-title">
            <h2>Software Development</h2>
            <p>Custom web applications, portals, and mobile apps built from the ground up</p>
          </div>

          <div className="portfolio-filters">
            {softwareFilters.map(filter => (
              <button
                key={filter.key}
                className={`filter-btn ${softwareFilter === filter.key ? 'active' : ''}`}
                onClick={() => setSoftwareFilter(filter.key)}
              >
                {filter.name}
              </button>
            ))}
          </div>
          
          <div className="software-grid">
            {filteredSoftwareProjects.map(project => (
              <div className="software-card" key={project.id}>
                <div 
                  className="software-image"
                  style={{ backgroundImage: `url(${project.image})` }}
                >
                  <div className="software-overlay">
                    <span className="category-badge">{project.category.toUpperCase()}</span>
                  </div>
                </div>
                <div className="software-content">
                  <h3>{project.title}</h3>
                  <p className="client-label">{project.client}</p>
                  <p className="description">{project.description}</p>
                  
                  {project.features && (
                    <div className="features-list">
                      {project.features.map((feature, i) => (
                        <span key={i} className="feature-tag">{feature}</span>
                      ))}
                    </div>
                  )}
                  
                  <div className="tech-stack">
                    {project.technologies.map((tech, i) => (
                      <span key={i} className="tech-tag">{tech}</span>
                    ))}
                  </div>
                  
                  {project.url && (
                    <a 
                      href={project.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="btn btn-primary btn-sm"
                    >
                      Visit Site →
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="software-cta">
            <h3>Need a Custom Solution?</h3>
            <p>From websites to full-featured business portals with mobile apps, we build solutions tailored to your needs.</p>
            <Link to="/contact" className="btn btn-primary">Discuss Your Project</Link>
          </div>
        </div>
      </section>

      {/* Installation Projects Section */}
      <section className="section section-gray">
        <div className="container">
          <div className="section-title">
            <h2>Installation Projects</h2>
            <p>Audio/Video, Security, and Automation installations</p>
          </div>

          <div className="portfolio-filters">
            {serviceFilters.map(filter => (
              <button
                key={filter.key}
                className={`filter-btn ${activeFilter === filter.key ? 'active' : ''}`}
                onClick={() => setActiveFilter(filter.key)}
              >
                {filter.name}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="loading">Loading projects...</div>
          ) : filteredProjects.length === 0 ? (
            <div className="no-projects">
              <p>Installation project gallery coming soon!</p>
            </div>
          ) : (
            <div className="portfolio-grid">
              {filteredProjects.map(project => (
                <Link to={`/portfolio/${project.slug}`} className="portfolio-card" key={project.id}>
                  <div 
                    className="portfolio-image"
                    style={{ backgroundImage: `url(${project.featured_image_url || 'https://via.placeholder.com/600x400'})` }}
                  >
                    <div className="portfolio-overlay">
                      <span className="view-btn">View Project</span>
                    </div>
                    {project.is_featured && <span className="featured-badge">Featured</span>}
                  </div>
                  <div className="portfolio-info">
                    <h3>{project.title}</h3>
                    {project.client_name && <p className="client-name">{project.client_name}</p>}
                    {project.services_provided && project.services_provided.length > 0 && (
                      <div className="portfolio-tags">
                        {project.services_provided.slice(0, 3).map((tag, i) => (
                          <span key={i} className="portfolio-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
